import { tr } from 'react-hook-form-auto'

import styles from './MealTimeline.sass'
import {
  PX_PER_SECOND,
  axisTicks,
  assignLanes,
  barWidth,
  colorIndex,
  endOf,
  formatTime,
  segmentHeight,
  stripHeight
} from './timelineShared'

const summary = (segment) => {
  const start = formatTime(segment.start || 0)
  const line = segment.kind == 'interval'
    ? `${start} · ${Math.round(segment.duration || 0)}s`
    : start

  return [ segment.label, line ].filter(Boolean).join(' · ')
}

const ReadBar = ({ segment, ingredients, done, onToggleDone }) => {
  const colorClass = styles['color' + colorIndex(segment.id)]
  const ingNames = (segment.ingredientIds || [])
    .map(id => ingredients.find(ing => ing.id == String(id)))
    .filter(Boolean)

  return (
    <div
      className={`${styles.segBar} ${colorClass} ${done ? styles.done : ''}`}
      style={{ height: segmentHeight(segment) }}
      title={`${summary(segment)} — ${tr('models.meal.timeline.done')}`}
      role="button"
      aria-pressed={done}
      onClick={() => onToggleDone(segment.id)}
    >
      <div className={styles.barContent}>
        {segment.label &&
          <span className={styles.barLabel}>{segment.label}</span>}
        {ingNames.length > 0 &&
          <div className={styles.barIngredients}>
            {ingNames.map(ing =>
              <span key={ing.id} className={styles.segIng}>
                {ing.name}
              </span>)}
          </div>}
      </div>
    </div>
  )
}

const ReadSegment = ({ segment, ingredients, done, onToggleDone }) =>
  <div
    className={styles.segWrap}
    style={{
      left: (segment.start || 0) * PX_PER_SECOND,
      width: barWidth(segment)
    }}
  >
    <ReadBar
      segment={segment}
      ingredients={ingredients}
      done={done}
      onToggleDone={onToggleDone}
    />
  </div>

export const MealTimelineRead = ({
  timeline = [],
  ingredients = [],
  doneIds,
  onToggleDone
}) => {
  const intervals = timeline.filter(s => s.kind == 'interval')
  const punctuals = timeline.filter(s => s.kind == 'punctual')

  const lanes = assignLanes(intervals)
  const maxEnd = timeline.reduce((max, s) => Math.max(max, endOf(s)), 0)
  const canvasWidth = Math.max(420, maxEnd * PX_PER_SECOND + 240)

  const { majorTicks, minorTicks } = axisTicks(maxEnd)

  return (
    <div className={styles.read}>
      <div className={styles.canvas} style={{ width: canvasWidth }}>
        <div className={styles.axis}>
          {minorTicks.map(t =>
            <div
              key={t}
              className={styles.tickMinor}
              style={{ left: t * PX_PER_SECOND }}
            />)}
          {majorTicks.map(t =>
            <div
              key={t}
              className={styles.tick}
              style={{ left: t * PX_PER_SECOND }}
            >
              <span className={styles.tickLabel}>{formatTime(t)}</span>
            </div>)}
        </div>
        {punctuals.length > 0 &&
          <div
            className={styles.markerStrip}
            style={{ height: stripHeight(punctuals) }}
          >
            {punctuals.map(segment =>
              <ReadSegment
                key={segment.id}
                segment={segment}
                ingredients={ingredients}
                done={doneIds && doneIds.has(segment.id)}
                onToggleDone={onToggleDone}
              />)}
          </div>}
        {lanes.map((lane, idx) =>
          <div
            key={idx}
            className={styles.lane}
            style={{ height: stripHeight(lane) }}
          >
            {lane.map(segment =>
              <ReadSegment
                key={segment.id}
                segment={segment}
                ingredients={ingredients}
                done={doneIds && doneIds.has(segment.id)}
                onToggleDone={onToggleDone}
              />)}
          </div>)}
      </div>
    </div>
  )
}
